import supabase from '../services/supabaseClient';

export const MEAL_MANAGER_LOGIN_URL = 'https://www.menu.maap.care/login';
export const ACTIVITY_PLANNER_LOGIN_URL = 'https://www.activities.maap.care/login';

export const RECOVERY_PENDING_KEY = 'maap.password_recovery_pending';

/**
 * Redirect URLs that must be listed in Supabase Auth → URL Configuration.
 * Shared project: Meal Manager + Activity Planner use one Site URL, so every
 * app origin has to be on the allow list or recovery emails fall back to
 * the Activity Planner Site URL and PKCE exchange fails.
 */
export const REQUIRED_AUTH_REDIRECT_URLS = [
  'https://app.maap.care/**',
  'https://app.maap.care/reset-password',
  'https://www.app.maap.care/**',
  'https://www.app.maap.care/reset-password',
  'https://www.activities.maap.care/**',
  'https://www.activities.maap.care/reset-password',
  'https://activities.maap.care/**',
  'https://www.menu.maap.care/**',
  'https://www.menu.maap.care/reset-password',
  'https://menu.maap.care/**',
];

export function getPasswordResetRedirectTo() {
  const origin = String(window.location.origin || '').replace(/\/$/, '');
  return `${origin}/reset-password`;
}

export function markPasswordRecoveryPending() {
  try {
    sessionStorage.setItem(RECOVERY_PENDING_KEY, '1');
  } catch {
    // ignore
  }
}

export function clearPasswordRecoveryPending() {
  try {
    sessionStorage.removeItem(RECOVERY_PENDING_KEY);
  } catch {
    // ignore
  }
}

export function isPasswordRecoveryPending() {
  try {
    return sessionStorage.getItem(RECOVERY_PENDING_KEY) === '1';
  } catch {
    return false;
  }
}

function parseRecoveryUrl() {
  if (typeof window === 'undefined') {
    return { code: null, accessToken: null, refreshToken: null, type: null, looksLikeRecovery: false };
  }
  const url = new URL(window.location.href);
  const hash = new URLSearchParams(url.hash.replace(/^#/, ''));
  const code = url.searchParams.get('code');
  const accessToken = hash.get('access_token');
  const refreshToken = hash.get('refresh_token');
  const type = hash.get('type') || url.searchParams.get('type');
  const looksLikeRecovery = Boolean(
    code
    || accessToken
    || type === 'recovery'
  );
  return { code, accessToken, refreshToken, type, looksLikeRecovery };
}

/**
 * Call synchronously on boot, before any await, so a recovery hash/code is
 * recorded even if the Supabase client strips it from the URL.
 */
export function captureRecoveryIntentFromUrl() {
  const { looksLikeRecovery } = parseRecoveryUrl();
  if (looksLikeRecovery) {
    markPasswordRecoveryPending();
    return true;
  }
  return isPasswordRecoveryPending();
}

function stripAuthParamsFromUrl() {
  if (typeof window === 'undefined') return;
  const url = new URL(window.location.href);
  url.searchParams.delete('code');
  url.searchParams.delete('type');
  url.hash = '';
  window.history.replaceState({}, document.title, `${url.pathname}${url.search}`);
}

/**
 * Establish the recovery session from ?code= (PKCE) or hash tokens (implicit).
 * A recovery session is NOT a completed login — callers must keep the pending flag
 * until the password is changed, then sign out.
 */
export async function consumeRecoverySession(client) {
  const auth = client || supabase;
  if (!auth) {
    return { session: null, error: { message: 'Password reset is not configured.' } };
  }

  const { code, accessToken, refreshToken, type, looksLikeRecovery } = parseRecoveryUrl();
  if (looksLikeRecovery) {
    markPasswordRecoveryPending();
  }

  let session = null;
  let error = null;

  if (code) {
    const result = await auth.auth.exchangeCodeForSession(code);
    session = result.data?.session || null;
    error = result.error || null;
  } else if (accessToken && refreshToken) {
    const result = await auth.auth.setSession({
      access_token: accessToken,
      refresh_token: refreshToken,
    });
    session = result.data?.session || null;
    error = result.error || null;
  }

  if (!session) {
    const existing = await auth.auth.getSession();
    session = existing.data?.session || null;
  }

  if (session && (looksLikeRecovery || type === 'recovery' || isPasswordRecoveryPending())) {
    markPasswordRecoveryPending();
  }

  if (session && (code || accessToken)) {
    stripAuthParamsFromUrl();
  }

  return { session, error: session ? null : error };
}

export async function abandonRecoverySession(client) {
  const auth = client || supabase;
  if (auth) {
    try {
      await auth.auth.signOut();
    } catch {
      // ignore
    }
  }
  clearPasswordRecoveryPending();
}

/**
 * Send a recovery email using this app's Supabase client so PKCE verifier
 * storage matches the client that will open the reset link.
 */
export async function sendPasswordResetEmail(email) {
  const trimmed = String(email || '').trim();
  if (!trimmed) {
    return { data: null, error: { message: 'Please enter your email address' } };
  }
  if (!supabase) {
    return { data: null, error: { message: 'Password reset is not configured.' } };
  }

  return supabase.auth.resetPasswordForEmail(trimmed, {
    redirectTo: getPasswordResetRedirectTo(),
  });
}
